<script>
import {useTrainingPlanStore} from "@/stores/Training/TrainingPlan.js"
import useSharedData from "@/use/SharedData.js";
import useSharedMethod from "@/use/SharedMethod.js";
import useTools from "@/use/Tools.js";
import {onMounted, watch} from "vue";
import {useSecurityStore} from "@/stores/Security.js";
import axios from "axios";

export default {
    setup() {
        const {total, loading, filter} = useSharedData();

        const {priceFormat, formatedDate, transformFilterValues} = useSharedMethod();

        const {$i18n, $router} = useTools();

        const trainingPlanStore = useTrainingPlanStore();
        const securityStore = useSecurityStore();

        const headers = [
            {
                text: "...",
                align: "left",
                sortable: false,
                value: "action",
                width: "0%"
            },
            {
                text: $i18n.t("common.code"),
                align: "left",
                sortable: true,
                value: "code",
                width: "0%"
            },
            {
                text: $i18n.t("common.label"),
                align: "left",
                sortable: true,
                value: "name",
                width: "0%"
            },
            {
                text: $i18n.t("common.year"),
                align: "left",
                sortable: true,
                value: "year",
                width: "0%"
            },
            {
                text: $i18n.t("common.from"),
                align: "left",
                sortable: true,
                value: "start",
                width: "0%"
            },
            {
                text: $i18n.t("common.to"),
                align: "left",
                sortable: true,
                value: "end",
                width: "0%"
            },
            {
                text: $i18n.t("training.price_prev"),
                align: "left",
                sortable: true,
                value: "price",
                width: "0%"
            },
            {
                text: $i18n.t("training.total_consume"),
                align: "left",
                sortable: true,
                value: "acceptedPrice",
                width: "0%"
            },
            {
                text: $i18n.t("training.total_remain"),
                align: "left",
                sortable: false,
                value: "totalRemain",
                width: "0%"
            },
            {
                text: $i18n.t("training.total_price"),
                align: "left",
                sortable: true,
                value: "totalPrice",
                width: "0%"
            },
            {
                text: $i18n.t("common.comment"),
                align: "left",
                sortable: true,
                value: "observation",
            },
        ];

        const actions = [
            {
                title: $i18n.t("common.edit"),
                icon: 'mdi-pencil-box-multiple',
                color: 'orange',
                access: () => securityStore.authorities['UPDATE_TRAINING_PLAN'],
                disabled: (item) => !item.open,
                func: (item) => {
                    $router.push({name: `training_plans.edit`, params: {id: item.id}})
                },
            },
            {
                title: $i18n.t("common.lock"),
                icon: 'mdi-lock',
                color: 'grey darken-2',
                access: () => securityStore.authorities['UPDATE_TRAINING_PLAN'],
                disabled: (item) => !item.open,
                func: (item) => {
                    $router.push({name: `training_plans.lock`, params: {id: item.id}})
                },
            },
            {
                title: $i18n.t("common.delete"),
                icon: 'mdi-trash-can',
                color: 'red',
                access: () => securityStore.authorities['DELETE_TRAINING_PLAN'],
                disabled: (item) => !item.open,
                func: (item) => {
                    $router.push({name: `training_plans.delete`, params: {id: item.id}})
                },
            },
        ];

        function findAll() {
            loading.value = true;
            axios.get('/trainingPlans', {params: transformFilterValues(filter.value)}).then((response) => {
                trainingPlanStore.setTrainingPlans(response.data);
                total.value = response.data.length;
            }).finally(() => {
                loading.value = false;
            });
        }

        onMounted(() => findAll());

        watch(() => filter.value, () => {
            findAll();
        }, {deep: true, immediate: true});

        function view(item) {
            $router.push({name: `training_plan_details`, params: {planId: item.id}});
        }

        return {
            trainingPlanStore,
            securityStore,
            headers,
            actions,
            total,
            loading,
            filter,
            priceFormat,
            formatedDate,
            view,
            findAll,
        };
    },
};
</script>

<template>
    <div>
        <w-app
            :title="$t('module.training_plan')"
            :total="total"
        >
            <template v-slot:rightToolbar>
                <w-btn-reload
                    :loading="loading"
                    @press="findAll"
                />
                <w-btn-add
                    :show="securityStore.authorities['SAVE_TRAINING_PLAN']"
                    @press="$router.push({ name: `training_plans.add` })"
                />
            </template>

            <template v-slot:content>
                <v-container fluid>
                    <v-row dense>
                        <v-col cols="3">
                            <w-text-field
                                v-model="filter.name"
                                :label="$t('common.name')"
                            />
                        </v-col>
                    </v-row>
                </v-container>
                <w-data-table
                    :headers="headers"
                    :actions="actions"
                    :items="trainingPlanStore.trainingPlans"
                    :total="total"
                    :loading="loading"
                    sort-by="name"
                    @rowDbClick="view"
                >

                    <template v-slot:[`item.code`]="{ item }">
                        <w-chip :text="item.code"/>
                    </template>
                    <template v-slot:[`item.name`]="{ item }">
                        <w-inline-text bold :text="item.name"/>
                    </template>
                    <template v-slot:[`item.year`]="{ item }">
                        <w-inline-text :text="item.year"/>
                    </template>
                    <template v-slot:[`item.start`]="{ item }">
                        <w-inline-text :text="formatedDate(item.start)"/>
                    </template>
                    <template v-slot:[`item.end`]="{ item }">
                        <w-inline-text :text="formatedDate(item.end)"/>
                    </template>
                    <template v-slot:[`item.price`]="{ item }">
                        <w-inline-text
                            clazz="orange--text"
                            bold
                            :text="priceFormat(item.price)"
                        />
                    </template>
                    <template v-slot:[`item.acceptedPrice`]="{ item }">
                        <w-inline-text
                            clazz="teal--text"
                            bold
                            :text="priceFormat(item.acceptedPrice)"
                        />
                    </template>
                    <template v-slot:[`item.totalRemain`]="{ item }">
                        <w-inline-text
                            clazz="red--text"
                            bold
                            :text="priceFormat(item.totalRemain)"
                        />
                    </template>
                    <template v-slot:[`item.totalPrice`]="{ item }">
                        <w-inline-text
                            clazz="blue--text"
                            bold
                            :text="priceFormat(item.totalPrice)"
                        />
                    </template>
                </w-data-table>
            </template>
        </w-app>
        <router-view/>
    </div>
</template>
